import { useOutletContext } from 'react-router';

import { useRevenueShareTermsGate } from 'hooks/useRevenueShareTermsGate';

import { ButtonGroupProvider } from 'components/buttonGroup/ButtonGroupContext';
import { OutletContextDeviceType } from 'components/layout/model';
import StargazerGate from 'components/provider/StargazerGate';
import withErrorBoundary from 'components/provider/withErrorBoundary';
import { TabListProvider } from 'components/tabList/TabListContext';
import Flex from 'components/util/Flex';

import CreatorInformationContainer from './components/creatorInformation/CreatorInformationContainer';
import DashboardSkeleton from './components/DashboardSkeleton';
import MinimumGuaranteeContainer from './components/minimumGuarantee/MinimumGuaranteeContainer';
import MonthlyRevenueRankingContainer from './components/monthlyRevenueRanking/MonthlyRevenueRankingContainer';
import RevenueChartContainer from './components/revenueChart/RevenueChartContainer';
import RevenueInformationContainer from './components/revenueInformation/RevenueInformationContainer';
import RisingStarBonusContainer from './components/risingStarBonus/RisingStarBonusContainer';
import StargazerBonusHistory from './components/risingStarBonus/StargazerBonusHistory';
import { rankingAssetOwner, rankingAssetType } from './constants/rankingType';
import { chartAssetTypes, chartPeriodTypes } from './constants/revenueChartType';

function DashBoardPage() {
  const { device } = useOutletContext<OutletContextDeviceType>();

  // 수익분배약관 미동의 기존 유저 진입 시 동의 모달 노출
  const { isResolved, needsAgreement } = useRevenueShareTermsGate();

  // 인증 정보 확정 전(로딩) 또는 약관 미동의(모달 노출) 동안은 실제 수익 데이터 대신 스켈레톤을 노출한다.
  if (!isResolved || needsAgreement) return <DashboardSkeleton isDesktop={device.isDesktop} />;

  return (
    <Flex rowGap={8} direction="column">
      {/* 별지기 MG Section — 별지기가 아니면 아무것도 그리지 않는다 (#2182) */}
      <StargazerGate>
        <Flex isExpanded>
          <MinimumGuaranteeContainer />
        </Flex>
        {/* 산정 후 별지기로 승급해 받은 샛별 보너스 — 적립 내역만 (10/6 기획 답변 D) */}
        <StargazerBonusHistory />
      </StargazerGate>
      {/* 샛별 달성 보너스 Section — 서버가 이번 달 정보(current)를 줄 때만 그린다 (studio#2524).
          등급 게이트가 없는 건 미지급 달 노출 때문(10/23 기획 답변 A안). 상단 블록 배치는 기획 답변(10/6) */}
      <RisingStarBonusContainer />
      {/* User Information Section */}
      <Flex
        columnGap={device.isDesktop ? 8 : 0}
        rowGap={device.isDesktop ? 0 : 8}
        isExpanded
        direction={device.isDesktop ? 'row' : 'column'}
      >
        <RevenueInformationContainer />
        <CreatorInformationContainer />
      </Flex>
      {/* Revenue Chart Section */}
      <Flex isExpanded>
        <ButtonGroupProvider
          buttonGroup={chartPeriodTypes}
          storage={{
            name: 'revenueChartButtonGroup',
            type: 'session',
          }}
        >
          <TabListProvider
            tabList={chartAssetTypes}
            storage={{
              name: 'revenueChartTab',
              type: 'session',
            }}
          >
            <RevenueChartContainer />
          </TabListProvider>
        </ButtonGroupProvider>
      </Flex>
      {/* Asset Section */}
      <Flex isExpanded>
        <ButtonGroupProvider
          buttonGroup={rankingAssetType}
          storage={{
            name: 'revenueRankingButtonGroup',
            type: 'session',
          }}
        >
          <TabListProvider
            tabList={rankingAssetOwner}
            storage={{
              name: 'revenueRankingTab',
              type: 'session',
            }}
          >
            <MonthlyRevenueRankingContainer />
          </TabListProvider>
        </ButtonGroupProvider>
      </Flex>
    </Flex>
  );
}

export default withErrorBoundary(DashBoardPage);
